import React, { useEffect, useRef } from 'react';
import './TouchAnimation.css';

const TouchAnimation = ({ isDarkMode }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to fullscreen
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle creation for the liquid light trail
    const createParticle = (x, y, isDarkMode) => {
      const size = Math.random() * 8 + 4;
      const life = 1.0;

      // Select colors that match the new mesh gradient theme
      const lightColors = ['#ff7b54', '#ff5d5d', '#ffdb8b'];
      const darkColors = ['#00f0ff', '#8a2be2', '#00a8ff'];

      const palette = isDarkMode ? darkColors : lightColors;
      const color = palette[Math.floor(Math.random() * palette.length)];

      particlesRef.current.push({
        x,
        y,
        size,
        life,
        color,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5
      });
    };

    // --- GLOBAL EVENT LISTENERS ---
    // Instead of attaching to the canvas (which is pointer-events: none),
    // we attach to the global window with { passive: true } so we NEVER block scrolling or clicking.

    const handleTouchMove = (e) => {
      // Loop through all active touches (multi-touch support)
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];

        // Create 2-3 particles per touch move point to make a dense, liquid trail
        for (let j = 0; j < 3; j++) {
          // Slight randomized offset so the trail looks natural and thick
          createParticle(
            touch.clientX + (Math.random() - 0.5) * 20,
            touch.clientY + (Math.random() - 0.5) * 20,
            isDarkMode
          );
        }
      }
    };

    // Attach passive listeners to the window
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    // Animation Loop
    let animationFrameId;
    const animate = () => {
      // Clear frame
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // We don't want to save/restore the whole context over and over, we use globalCompositeOperation
      // 'screen' or 'lighter' creates a beautiful blooming effect when particles overlap
      ctx.globalCompositeOperation = 'lighter';

      for (let i = 0; i < particlesRef.current.length; i++) {
        const p = particlesRef.current[i];

        // Move particle
        p.x += p.vx;
        p.y += p.vy;

        // Shrink and fade
        p.size *= 0.95;
        p.life -= 0.03;

        // Draw particle as a glowing orb
        if (p.life > 0 && p.size > 0.5) {
          ctx.beginPath();
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
          // Center is solid, edges are transparent
          gradient.addColorStop(0, p.color);
          gradient.addColorStop(1, 'transparent');

          ctx.fillStyle = gradient;
          ctx.globalAlpha = p.life;
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Remove dead particles
      particlesRef.current = particlesRef.current.filter((p) => p.life > 0 && p.size > 0.5);

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('touchmove', handleTouchMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="touch-animation-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none', // VERY IMPORTANT: Allows clicks/scrolls to pass through exactly to the DOM
        zIndex: 9999, // On top of everything
      }}
    />
  );
};

export default TouchAnimation;
